/**
 * DashboardPage — Steward Admin
 * Overview of active championship, drivers, upcoming events, recent penalties.
 */
import React, { useState, useEffect } from 'react';
import {
  onDrivers, onEvents, onPenalties, TITLES,
  sendChampionshipAnnouncement, sendEventReminder,
} from '../services/tripleCrown';
import useChampionship from '../hooks/useChampionship';

export default function DashboardPage() {
  const { champ, champs, active, selectChamp } = useChampionship();
  const [drivers, setDrivers] = useState([]);
  const [events, setEvents] = useState([]);
  const [penalties, setPenalties] = useState([]);

  useEffect(() => {
    if (!champ) { setDrivers([]); setEvents([]); setPenalties([]); return; }
    const unsubs = [
      onDrivers(champ.id, setDrivers),
      onEvents(champ.id, setEvents),
      onPenalties(champ.id, setPenalties),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [champ]);

  const upcoming = events.filter(e => e.status === 'upcoming');
  const completed = events.filter(e => e.status === 'completed');
  const activePenalties = penalties.filter(p => p.status === 'active');

  const nextEvent = upcoming[0];
  const TZ = 'America/Barbados';
  const nextDate = nextEvent?.scheduledDate?.toDate
    ? nextEvent.scheduledDate.toDate().toLocaleDateString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: TZ,
      })
    : '—';

  // ── Notification sender state ──
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifMessage, setNotifMessage] = useState('');
  const [notifEventId, setNotifEventId] = useState('');   // optional: open this event on tap
  const [notifSending, setNotifSending] = useState(false);
  const [notifResult, setNotifResult] = useState(null);

  const handleSendNotification = async () => {
    if (!notifTitle.trim() || !notifMessage.trim() || !champ) return;
    setNotifSending(true);
    setNotifResult(null);
    try {
      await sendChampionshipAnnouncement({
        championshipId: champ.id,
        title: notifTitle.trim(),
        message: notifMessage.trim(),
        ...(notifEventId ? { eventId: notifEventId } : {}),
      });
      const activeCount = drivers.filter(d => d.status === 'active').length;
      setNotifResult({ type: 'success', msg: `Sent to ${activeCount} active driver${activeCount !== 1 ? 's' : ''}` });
      setNotifTitle('');
      setNotifMessage('');
      setNotifEventId('');
    } catch (err) {
      setNotifResult({ type: 'error', msg: err?.message || 'Failed to send notification' });
    } finally {
      setNotifSending(false);
    }
  };

  const handleEventReminder = async () => {
    if (!nextEvent || !champ) return;
    setNotifSending(true);
    setNotifResult(null);
    try {
      await sendEventReminder({ championshipId: champ.id, eventId: nextEvent.id });
      const activeCount = drivers.filter(d => d.status === 'active').length;
      setNotifResult({ type: 'success', msg: `Reminder sent to ${activeCount} driver${activeCount !== 1 ? 's' : ''}` });
    } catch (err) {
      setNotifResult({ type: 'error', msg: err?.message || 'Failed to send reminder' });
    } finally {
      setNotifSending(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            {champ ? champ.title : 'No active championship'}
            {active.length > 1 && ` · ${active.length} championships active`}
          </p>
        </div>
        {champs.length > 1 && (
          <select
            className="form-select"
            value={champ?.id || ''}
            onChange={e => selectChamp(e.target.value)}
            style={{ maxWidth: 220, fontSize: 13 }}
          >
            {champs.map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        )}
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <p className="stat-label">Drivers</p>
          <p className="stat-value">{drivers.length}</p>
          <p className="stat-sub">{drivers.filter(d => d.status === 'active').length} active</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Events</p>
          <p className="stat-value">{events.length}</p>
          <p className="stat-sub">{completed.length} completed</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Upcoming</p>
          <p className="stat-value">{upcoming.length}</p>
          <p className="stat-sub">{nextEvent ? `Next: ${nextDate}` : 'None scheduled'}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Penalties</p>
          <p className="stat-value" style={{ color: activePenalties.length > 0 ? 'var(--danger)' : undefined }}>
            {activePenalties.length}
          </p>
          <p className="stat-sub">{penalties.length} total issued</p>
        </div>
      </div>

      {/* Send Notification */}
      {champ && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header">
            <h3 className="card-title">Send Notification</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              {nextEvent && (
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={handleEventReminder}
                  disabled={notifSending}
                  title={`Send reminder for: ${TITLES.find(t => t.id === nextEvent.titleId)?.name || nextEvent.titleId}`}
                >
                  ⏰ Send Event Reminder
                </button>
              )}
              <button
                className="btn btn-sm btn-primary"
                onClick={() => setNotifOpen(!notifOpen)}
              >
                {notifOpen ? 'Cancel' : '📣 Custom Message'}
              </button>
            </div>
          </div>

          {notifResult && (
            <div style={{
              padding: '8px 14px',
              marginBottom: notifOpen ? 12 : 0,
              borderRadius: 8,
              fontSize: 13,
              background: notifResult.type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(255, 45, 120, 0.1)',
              color: notifResult.type === 'success' ? '#22c55e' : '#ff2d78',
              border: `1px solid ${notifResult.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255, 45, 120, 0.2)'}`,
            }}>
              {notifResult.type === 'success' ? '✅ ' : '❌ '}{notifResult.msg}
            </div>
          )}

          {notifOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Title</label>
                <input
                  className="form-input"
                  value={notifTitle}
                  onChange={e => setNotifTitle(e.target.value)}
                  placeholder="e.g. Schedule Update, Important Announcement"
                  maxLength={100}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Message</label>
                <textarea
                  className="form-input"
                  value={notifMessage}
                  onChange={e => setNotifMessage(e.target.value)}
                  placeholder="Write your message to all active drivers..."
                  rows={3}
                  maxLength={500}
                  style={{ resize: 'vertical', minHeight: 60 }}
                />
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                  {notifMessage.length}/500 · Sent via push notification to {drivers.filter(d => d.status === 'active').length} active drivers
                </span>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Link to event (optional)</label>
                <select className="form-select" value={notifEventId} onChange={e => setNotifEventId(e.target.value)}>
                  <option value="">None — opens the Championships page</option>
                  {[...events]
                    .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999))
                    .map(ev => (
                      <option key={ev.id} value={ev.id}>
                        {(TITLES.find(t => t.id === ev.titleId)?.short || ev.titleId || '')} · {ev.phaseName || ev.roundName || 'Event'}
                      </option>
                    ))}
                </select>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                  Tapping the notification opens this event in the app.
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSendNotification}
                  disabled={notifSending || !notifTitle.trim() || !notifMessage.trim()}
                >
                  {notifSending ? 'Sending...' : 'Send to All Drivers'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upcoming Events */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header">
          <h3 className="card-title">Upcoming Events</h3>
        </div>
        {upcoming.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No upcoming events scheduled</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Phase</th>
                  <th>Track</th>
                  <th>Date</th>
                  <th>Scoring</th>
                </tr>
              </thead>
              <tbody>
                {upcoming.slice(0, 5).map(evt => {
                  const title = TITLES.find(t => t.id === evt.titleId);
                  const date = evt.scheduledDate?.toDate
                    ? evt.scheduledDate.toDate().toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: TZ,
                      })
                    : '—';
                  return (
                    <tr key={evt.id}>
                      <td>{title?.name || evt.titleId}</td>
                      <td><span className="badge badge-teal">{evt.phaseName || `Phase ${evt.phase}`}</span></td>
                      <td>{evt.track || '—'}</td>
                      <td>{date}</td>
                      <td><span className="badge badge-muted">{evt.scoringMode || 'position'}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Penalties */}
      {activePenalties.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Active Penalties</h3>
            <span className="badge badge-pink">{activePenalties.length} pending</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Driver</th>
                  <th>Points</th>
                  <th>Reason</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {activePenalties.slice(0, 5).map(pen => {
                  const driver = drivers.find(d => d.id === pen.driver);
                  return (
                    <tr key={pen.id}>
                      <td>{driver?.name || pen.driver}</td>
                      <td style={{ color: 'var(--danger)', fontWeight: 600 }}>-{pen.points}</td>
                      <td>{pen.reason}</td>
                      <td><span className="badge badge-pink">{pen.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
